/**
 * This component is used to set MPIN for a OLD USER and new USER
 * OLD UDER : MPIN, CONFIRM MPIN
 * NEW USER : DATE OF BIRTH, MPIN, CONFIRM MPIN
 */

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import { Spinner, Button, InputGroup, Form } from "react-bootstrap";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";

import Background from "../BackGround/background";
import MadeInLoveIndia from "../common/madeInLoveIndia";
import Timer from "../common/timer";
import authService from "../../services/authService";
import flowBackEndServices2 from "../../services/flowBackendServices";
import {
  loginEvent,
  logout,
  setEmailAndMobile,
  setOtpData,
  setUser,
  switchUserEvent,
} from "../../slices/authSlice";
import { getBrowserDetails } from "../../comman/status";

import refresh from "../../assets/images/refresh-circle.svg";
import "./mpin.css";
import { RootState } from "../../store";
import { getDeviceID, getExtraQueryparam } from "../common/parseQueryParam";
import type { LoginInfo } from "../../models/login";

import RiskDisclosure from "../LoginForm/RiskDisclosure";
import { t } from "../../utils/appString";
import ValidationList from "../ValidationList";
import moment from "moment";

import { ReactComponent as Calander } from "../../assets/images/calendar.svg";

// Constants for user types
const USER_TYPES = {
  OLD: "OLD",
  NEW: "NEW",
} as const;

const isAllSame = (value: string) => [...new Set(value)].length === 1;
const isSequential = (value: string) => {
  const increasing = "0123456789";
  const decreasing = "9876543210";
  return increasing.includes(value) || decreasing.includes(value);
};
// Zod validation schemas
const OldUserMpinSchema = z
  .object({
    mpin: z
      .string()
      .refine((value) => value.length === 4, "MPIN must be 4 digits")
      .refine((value) => /^\d+$/.test(value), t('validation.mpin.erroe.mpinShouldContainOnlyNumber'))
      .refine((value) => !isAllSame(value), t('validation.mpin.erroe.mpinShouldNotHaveAllSameDigits'))
      .refine(
        (value) => !isSequential(value),
        t('validation.mpin.erroe.mpinShouldNotHaveSequentialDigits')
      ),

    confirmMpin: z
      .string()
      // .refine((value) => value.length === 4, "Confirm MPIN must be 4 digits")
      // .refine(
      //   (value) => /^\d+$/.test(value),
      //   "Confirm MPIN must contain only numbers"
      // ),
  })
  .refine((data) => data.mpin === data.confirmMpin, {
    message: "MPIN didn’t match",
    path: ["confirmMpin"],
  });

const NewUserMpinSchema = z
  .object({
    dateOfBirth: z
      .string()
      .refine((value) => value.trim() !== "", "Date of Birth is required"),

    mpin: z
      .string()
      .refine((value) => value.length === 4, "MPIN must be 4 digits")
      .refine((value) => /^\d+$/.test(value), t('validation.mpin.erroe.mpinShouldContainOnlyNumber'))
      .refine((value) => !isAllSame(value), t('validation.mpin.erroe.mpinShouldNotHaveAllSameDigits'))
      .refine(
        (value) => !isSequential(value),
        t('validation.mpin.erroe.mpinShouldNotHaveSequentialDigits')
      ),

    confirmMpin: z
      .string()
      .refine((value) => value.length === 4, "Confirm MPIN must be 4 digits")
      .refine(
        (value) => /^\d+$/.test(value),
        "Confirm MPIN must contain only numbers"
      ),
  })
  .refine((data) => data.mpin === data.confirmMpin, {
    message: "MPINs do not match",
    path: ["confirmMpin"],
  });

type OldUserMpinFormData = z.infer<typeof OldUserMpinSchema>;
type NewUserMpinFormData = z.infer<typeof NewUserMpinSchema>;

interface UserData {
  user_id: string;
  user_name: string;
}

interface AuthData {
  mfaType: "otp" | "totp";
  deviceId: string;
  refreshToken: string;
}

const MpinInput: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user: userFromStore, themeData } = useSelector(
    (state: RootState) => state.authUser
  );

  const [showNext, setShowNext] = useState(false);
  const [kra, setKra] = useState<number>(0);
  const [showDot, setShowDot] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [userType, setUserType] = useState<string>("");

  const {
    user,
    data: storeData,
    isNewUser,
    isOTPVerified
  } = useSelector((state: RootState) => state.authUser);

  useEffect(() => {
    setUserType(isNewUser ? USER_TYPES.NEW : USER_TYPES.OLD);
  }, [isNewUser]);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const handleResize = () => {
    setIsDesktop(window.innerWidth > 768);
  };

  const handleLogoutToLogin = () => {
    dispatch(switchUserEvent());
    dispatch(authService.logout());
    navigate("/", { replace: true });
  };

  useEffect(() => {
    // lets check for local storage

    if(!isOTPVerified){
      const localVAl = localStorage.getItem("TV_ACS_TOKEN")
    if(localVAl){
      navigate("/apps",{ replace: true});
    }else {

      handleLogoutToLogin()
    }
    }
    
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [otp, setOtp] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingResed, setIsLoadingResed] = useState<boolean>(false);
  const [showResetBtn, setShowResetBtn] = useState<boolean>(false);
  const [otpType, setOtpType] = useState<"otp" | "totp">("otp");

  const [aeroUrl, setAeroUrl] = useState<string | null>(null);

  // Dynamic validation schema based on user type
  const validationSchema =
    userType === USER_TYPES.OLD ? OldUserMpinSchema : NewUserMpinSchema;

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<OldUserMpinFormData | NewUserMpinFormData>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues:
      userType === USER_TYPES.NEW
        ? {
            mpin: "",
            confirmMpin: "",
          }
        : {
            dateOfBirth: "",
            mpin: "",
            confirmMpin: "",
          },
  });

  const dateOfBirth =
    userType === USER_TYPES.OLD ? (watch("dateOfBirth") as string) : "";

  const mpin = watch("mpin");
  const confirmMpin = watch("confirmMpin");

  // Dynamic button disable logic
  const isButtonDisabled =
    isLoading ||
    mpin.length !== 4 ||
    confirmMpin.length !== 4 ||
    !isValid;

  const handleLogout = () => {
    dispatch(switchUserEvent());
    dispatch(authService.logout());
    navigate("/", { replace: true });
  };

  const showResend = () => {
    setShowResetBtn(!showResetBtn);
  };

  const loginWithOTP = (reset: boolean = false) => {
    if (!storeData) return;

    setIsLoadingResed(true);
    const { deviceId, refreshToken } = storeData;
    const sendData = { deviceId, mfaType: otpType };

    flowBackEndServices2
      .refreshLoginOtpSend(sendData, refreshToken)
      .then((value) => {
        if (value?.status === 200) {
          setOtp("");
          setOtpType("otp");
          if (reset) showResend();
          toast.success("OTP sent");
        } else if (
          value.data.status === "error" &&
          value.data.message === "session has expired"
        ) {
          handleLogout();
        }
      })
      .catch(() => {
        toast.error("Failed");
        setIsLoadingResed(false);
      })
      .finally(() => setIsLoadingResed(false));
  };

  const resendPassword = () => {
    loginWithOTP(true);
  };

  const switchToTotp = () => {
    setOtp("");
    setOtpType("totp");
  };

  const onSubmitRegistration = async (
    data: OldUserMpinFormData | NewUserMpinFormData
  ) => {

    // if (userType === USER_TYPES.OLD) { user.token
    let response: any;
    if (userType === USER_TYPES.OLD) {
      response = await flowBackEndServices2.setMpin(
        data,
        String(user?.access_token)
      );
    } else {
      if (themeData.verifySSO){
        response = await flowBackEndServices2.secondFactorSso(
          {
            dob: new Date(moment(watch("dateOfBirth"), "DD/MM/YYYY").format("YYYY-MM-DD")),
            mpin: "",
            newMpin: data.confirmMpin,
          },
          String(storeData?.token)
        );
      }else{
        response = await flowBackEndServices2.secondFactor(
          {
            dob: new Date(moment(watch("dateOfBirth"), "DD/MM/YYYY").format("YYYY-MM-DD")),
            mpin: "",
            newMpin: data.confirmMpin,
          },
          String(storeData?.token)
        );
      }
    }
    if (response.data.status === "success") {
      const redirecUrl = userType === USER_TYPES.OLD ? storeData?.redirectURL : response.data?.redirectUrl
      const val = userType === USER_TYPES.OLD ? userFromStore : response.data 
      setUpSessionData(val,redirecUrl);
    } else {
      // I need to show toast with message
      toast.error(`${response.data.message}`);
    }
  };

  const GotoLogin = () => {
    // remove user data form redux store
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const allCharactersMustNotBESame = (mpin: string) => {
    let mpinArray = mpin.split("");
    let uniqueArray = mpinArray.filter((item, index) => mpinArray.indexOf(item) === index);
    return mpinArray.length === uniqueArray.length;
  }

  return (
    <>
      <Background>
        <div
          style={{ maxWidth: 500 }}
          className="h-100 d-flex card_padding flex-column justify-content-between align-items-center"
        >
          <div className="w-100 d-flex flex-row justify-content-left align-items-center gap-3">
            <i
              className="fas fa-arrow-left fas fa-lg"
              onClick={GotoLogin}
              style={{ cursor: "pointer" }}
            />
            <span
              style={{ color: "#171D27", fontWeight: 700 }}
              className="h5 pb-0 mb-0"
            >
              Set up your MPIN now
            </span>
          </div>

          <div className="flex-fill w-100">
            <div className="pt-5 pb-3">
              We're transitioning to MPIN-based login for a more convenient
              login experience. MPINs are{" "}
              <span style={{ fontWeight: 700 }}>easier to remember</span> and
              enable <span style={{ fontWeight: 700 }}>quicker access</span> to
              your account.
            </div>

            {/* Conditionally render Date of Birth for NEW users */}
            {userType === USER_TYPES.NEW && (
              <div className="mb-3">
                <label
                  htmlFor="dateOfBirth"
                  className="form-label"
                  style={{ fontWeight: 600, color: "black" }}
                >
                  Date of Birth
                </label>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => {
                    const [inputType, setInputType] = useState<"text" | "date">(
                      "text"
                    );
                    const inputRef = useRef<HTMLInputElement>(null);
  
                    const formatDate = (inputDate: string) => {
                      const digits = inputDate.replace(/\D/g, "");
                      if (digits.length === 8) {
                        const formattedDate = moment(digits, "YYYYMMDD").format(
                          "DD/MM/YYYY"
                        );
                        if (moment(formattedDate, "DD/MM/YYYY", true).isValid()) {
                          return formattedDate;
                        }
                      }
                      return "";
                    };
  
                    return (
                      <InputGroup>
                        <Form.Control
                          {...field}
                          ref={inputRef}
                          type={inputType}
                          placeholder="DD/MM/YYYY"
                          value={value}
                          onFocus={() => {
                            setInputType("date");
                            setTimeout(() => inputRef.current?.showPicker(), 100); // Open calendar picker
                          }}
                          onBlur={(e) => {
                            const formattedDate = formatDate(e.target.value);
                            onChange(formattedDate);
                            setInputType("text");
                          }}
                          onChange={(e) => onChange(e.target.value)}
                          maxLength={10}
                          className={`dateofbirth ${
                            (errors as any).dateOfBirth ? "is-invalid" : ""
                          }`}
                          id="dateOfBirth"
                          style={{
                            paddingRight: "40px",
                            backgroundColor: "white",
                            color: "#495057",
                          }}
                        />
                        {!(errors as any).dateOfBirth && (
                          <InputGroup.Text
                            onClick={() => inputRef.current?.showPicker()} // Open date picker on click
                            style={{
                              position: "absolute",
                              right: 0,
                              zIndex: 12,
                              bottom: 5,
                              backgroundColor: "transparent",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            <Calander />
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                    );
                  }}
                  // render={({ field: { onChange, value, ...field } }) => {
                  //   <input
                  //     {...field}
                  //     type="date"
                  //     className={`form-control ${
                  //       (errors as any).dateOfBirth ? "is-invalid" : ""
                  //     }`}
                  //     id="dateOfBirth"
                  //   />
                  // )}
                />
                {(errors as any).dateOfBirth && (
                  <div className="invalid-feedback">
                    {(errors as any).dateOfBirth.message}
                  </div>
                )}
              </div>
            )}

            {/* MPIN Input */}
            <div className="mb-3">
              <div style={{ fontWeight: 600 }} className="colorBlack">
                Enter MPIN
              </div>
              <Controller
                name="mpin"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div
                    id="myInput"
                    className="w-100 d-flex flex-row align-items-center"
                  >
                    <OtpInput
                      inputStyle={`opt_box-hide ${errors.mpin ? "is-invalid" : ""}`}
                      value={value}
                      onChange={onChange}
                      numInputs={4}
                      isInputNum={true}
                      isInputSecure={false}
                      separator={<span />}
                    />
                  </div>
                )}
              />
               {<ValidationList isValid={isAllSame(watch('mpin'))  ?  false : (watch('mpin').length ===4) ? true : false} message={t('validation.mpin.erroe.mpinShouldNotHaveAllSameDigits')} />}
              { <ValidationList isValid={(watch('mpin').length ===4) ? true : false} message={t('validation.mpin.erroe.mpinShouldContainOnlyNumber')} />}
              {  <ValidationList isValid={isSequential(watch('mpin')) ?  false : (watch('mpin').length ===4)  ? true : false} message={t('validation.mpin.erroe.mpinShouldNotHaveSequentialDigits')} />}
            </div>

            {/* Confirm MPIN Input */}
            <div className="mb-3">
              <div style={{ fontWeight: 600 }} className="colorBlack">
                Confirm MPIN
              </div>
              <Controller
                name="confirmMpin"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div
                    id="myInput"
                    className="w-100 d-flex flex-row align-items-center"
                  >
                    <OtpInput
                      inputStyle={`opt_box-hide ${
                        errors.confirmMpin ? "is-invalid" : ""
                      }`}
                      value={value}
                      onChange={onChange}
                      numInputs={4}
                      isInputNum={true}
                      isInputSecure={false}
                      separator={<span />}
                    />
                  </div>
                )}
              />
              {(watch('confirmMpin').length === 4) && errors.confirmMpin && (
                <div className="text-danger mt-2">
                  {(errors.confirmMpin as any).message}
                </div>
              )}
            </div>

            <Button
              id="myBtn"
              disabled={isLoading || isButtonDisabled}
              className="blue_btn w-100 my-3"
              onClick={handleSubmit(onSubmitRegistration)}
              variant="primary"
              type="submit"
            >
              {isLoading && (
                <Spinner
                  className="mr-2"
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {t("buttonVarientText.setMpin")}
            </Button>
          </div>
        </div>
      </Background>
      {showDisclosure && getDisclosure()}
    </>
  );

  function acknowledge() {
    localStorage.setItem("acknowledge_disclosure", "false");
    sessionStorage.setItem("acknowledge_disclosure", "false");
    if (aeroUrl) {
      window.open(aeroUrl, "_self");
    } else {
      navigate("/apps", { replace: true });
    }
  }

  function moveToNext() {
    setShowNext(false);
  }

  function getDisclosure() {
    localStorage.setItem("acknowledge_disclosure", "true");
    sessionStorage.setItem("acknowledge_disclosure", "true"); //To block back from browser
    if (isDesktop) {
      return (
        <React.Fragment>
          <div className="disclosureLayer"></div>
          <div className="disclosure">
            {!showNext && <RiskDisclosure />}
            <div className="dot-container">
              {/* <div id="first-dot" className="cr-dot"></div> */}
              {showNext && <div id="second-dot" className="cr-dot"></div>}
            </div>
            {!showNext ? (
              <div className="discFooter">
                <button autoFocus type="button" className="ackButton c-pointer" onClick={acknowledge}>
                  I understand
                </button>
              </div>
            ) : (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={moveToNext}>
                  Next
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <div className="disclosureLayer"></div>
          <div className="disclosure" style={{ width: "300px" }}>
            {!showNext && <RiskDisclosure />}
            <div className="dot-container">
              {/* <div id="first-dot" className="cr-dot"></div> */}
              {showNext && <div id="second-dot" className="cr-dot"></div>}
            </div>
            {!showNext ? (
              <div className="discFooter">
                <button autoFocus type="button" className="ackButton c-pointer" onClick={acknowledge}>
                  I understand
                </button>
              </div>
            ) : (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={moveToNext}>
                  Next
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }
  }

  function setUpSessionData(value: any,url:string) {
    let userData = value;
      if (themeData.verifySSO) {
        const params = localStorage.getItem("Q_PARAM_ALL");
        const applicationId = params?.split("=");
        if (applicationId && applicationId[1] == 'at_g4FXzp00jB"') {
          if (value.kra_status == "HOLD") {
            setShowNext(true);
            setKra(1);
            setShowDot(true);
          } else if (value.kra_status == "REJECTED") {
            setShowNext(true);
            setKra(0);
            setShowDot(true);
          } else {
            setShowNext(false);
          }
          setShowDisclosure(true);
          setAeroUrl(url + getExtraQueryparam());
        } else {
          let fullRedirectUrl = url + getExtraQueryparam();
          window.open(fullRedirectUrl, "_self");
        }
        return;
      }
      if (userData.kra_status == "HOLD") {
        setShowNext(true);
        setKra(1);
        setShowDot(true);
      } else if (userData.kra_status == "REJECTED") {
        setShowNext(true);
        setKra(0);
        setShowDot(true);
      } else {
        setShowNext(false);
      }
      setShowDisclosure(true);

      if(userType===USER_TYPES.NEW){
        let newData = {
          ...storeData,
          refreshToken: userData.refreshToken,
        };
        let dataSet = { data: newData, user_id: storeData.clientCode };
        dispatch(setOtpData(dataSet));
        const refreshData = {
          refreshToken: userData.refreshToken || userData.refresh_token,
          ssoToken: userData.ssoToken,
          deviceId: storeData.deviceId,
          tradingActive: userData.data?.tradingActive,
        };
        const userModify = { ...userData.data, ...refreshData };
  
        // login event
        dispatch(setEmailAndMobile({ mobile: null, email: userModify.email }));
        dispatch(
          loginEvent({
            mobile: "",
            email: userModify.email,
          })
        );
        authService.saveUserSessionFlow(userModify, refreshData, dispatch);
      }
  }
};

export default MpinInput;
